import { Component, ReactNode } from 'react';

// Use plain old styles objects as this component needs to be self-contained
//  and not rely on global styles or other dependencies.
const headingStyles = {
  fontFamily: 'Inter, Arial, Helvetica, sans-serif',
  fontWeight: 600,
  lineHeight: '150%',
  fontSize: '1.75rem',
  textDecoration: 'none',
};
const textStyles = {
  fontFamily: 'Inter, Arial, Helvetica, sans-serif',
  fontWeight: 400,
  lineHeight: '150%',
  fontSize: '0.875rem',
  textDecoration: 'none',
};
const primaryButtonStyles = {
  ...textStyles,
  padding: '.75rem 1rem',
  backgroundColor: '#5760A8',
  border: 'solid',
  borderColor: '#5760A8',
  borderWidth: '1px',
  borderRadius: '4px',
  color: '#FFFFFF',
  boxShadow:
    '0px 19px 3px 0px #00000000, 0px 6px 2px 0px #00000003, 0px 3px 2px 0px #0000000d, 0px 1px 1px 0px #00000017, 0px 0px 1px 0px #0000001a',
};

const cardStyles = {
  padding: '1rem 0.5rem',
  backgroundColor: '#ffffff',
  borderRadius: '0.5rem',
  display: 'flex' as const,
  flexDirection: 'column' as const,
  boxShadow: '0 19px 3px #0000, 0 6px 2px #00000003, 0 3px 2px #0000000d, 0 1px 1px #00000017, 0 0 1px #0000001a',
  overflow: 'hidden',
  gap: '1rem',
  margin: 'auto',
  maxWidth: '480px',
};

const bgStyles = {
  backgroundColor: 'rgb(238, 239, 246)',
  minWidth: 'calc(100vw - 16px)', // Subtracting body margin
  minHeight: 'calc(100vh - 16px)',
  display: 'flex',
};

const linkStyles = {
  color: '#5760A8',
};

function clearAllCookies() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name] = cookie.split('=');
    // Clear for root path
    document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    // Clear for current path
    document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

function removeAuthStateLocalStorage() {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('@@auth0spajs@@')) {
      localStorage.removeItem(key);
    }
  }
}

const supportEmail = 'support@ocula.tech';

const messages = {
  title: 'Oops, something went wrong',
  reloadPrompt: 'Try reloading the app: ',
  reloadButton: 'Reload',
  reloadLogoutPrompt: 'Still not working? Try logging out: ',
  reloadLogoutButton: 'Log out & reload',
  supportInstruction: 'If this problem persists, please contact support at',
  supportLinkText: supportEmail,
  supportEmailSubject: 'P0: Ocula application is not available',
  supportOutcome: ' to get this resolved.',
};

type Props = { children?: ReactNode } & object;

/** Wraps the entire app to provide a UI and logging when a catastrophic error occurs. */
export class AppErrorBoundary extends Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(/* error: Error */) {
    return { hasError: true };
  }

  componentDidCatch(/* error, errorInfo */) {
    // TODO log the error
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div style={bgStyles}>
          <div style={cardStyles}>
            <h1 role="alert" style={headingStyles}>
              {messages.title}
            </h1>
            <p style={textStyles}>{messages.reloadPrompt}</p>
            <button style={primaryButtonStyles} type="button" onClick={() => globalThis.location.reload()}>
              {messages.reloadButton}
            </button>
            <p style={textStyles}>{messages.reloadLogoutPrompt}</p>
            <button
              style={primaryButtonStyles}
              type="button"
              onClick={() => {
                clearAllCookies();
                removeAuthStateLocalStorage();
                globalThis.location.reload();
              }}
            >
              {messages.reloadLogoutButton}
            </button>
            <p style={textStyles}>
              {messages.supportInstruction}{' '}
              <a href={`mailto:${supportEmail}?subject=${messages.supportEmailSubject}`} style={linkStyles}>
                {messages.supportLinkText}
              </a>
              {messages.supportOutcome}
            </p>
          </div>
        </div>
      );
    }
    return children;
  }
}
